import { addMinutes } from "date-fns";
import Close from "@mui/icons-material/Close";
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  IconButton,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useState } from "react";
import { Link } from "react-router-dom";

import { getPreferences } from "~/utils/eventsHelper";
import { useOrgConfig } from "~/providers/OrgConfigProvider";
import VisitDetailsTab from "./VisitDetailsTab";
import { useZonedDateFormat } from "~/providers/ZonedDateFormatProvider";
import { LocalVisit, VisitMetaData } from "~/hooks/visitDragDropReducer";
import { filterOutUndefined } from "~/utils/careTeamHelpers";

type Props = {
  visit: LocalVisit;
  visitMetaData: VisitMetaData;
  onClose: () => void;
  onRemoveVisit: (visit: LocalVisit) => void;
};

export default function VisitRequestPopup({
  visit,
  visitMetaData,
  onClose,
  onRemoveVisit,
}: Props) {
  const { timeLabelWithZone } = useZonedDateFormat();
  const [currentTab, setCurrentTab] = useState("details");

  const orgConfig = useOrgConfig();

  const visitResources = visit.visitResources

  const currentServiceLine = visitMetaData.serviceLineId ? orgConfig.serviceLines[visitMetaData.serviceLineId] : undefined;
  const currentVisitType = visit.visitTypeId ? orgConfig.visitTypes[visit.visitTypeId] : undefined;

  const visitTypeServices = currentVisitType?.visitTypeServices.filter(vts => vts.service?.id && visitMetaData.serviceIds?.includes(vts.service?.id));

  const preferences = getPreferences(currentServiceLine, currentVisitType);

  const arrivalWindow = visit.startTime
    ? (() => {
      const startTime = new Date(visit.startTime);
      let offset = preferences?.arrivalWindowOffsetMinutes;
      offset = offset || 30;
      return [
        addMinutes(startTime, -offset),
        addMinutes(startTime, offset)
      ] as [Date, Date];
    })()
    : null;

  // Create an object that matches the shape expected by VisitDetailsTab
  const adaptedVisit = {
    visitType: currentVisitType,
    arrivalWindowStartTime: arrivalWindow?.[0].toISOString(),
    arrivalWindowEndTime: arrivalWindow?.[1].toISOString(),
    addressTimezone: visit.addressTimezone,
    addressStreet: visitMetaData.address.addressStreet,
    addressCity: visitMetaData.address.addressCity,
    addressState: visitMetaData.address.addressState,
    addressZip: visitMetaData.address.addressZip,
    visitResources: visit.visitResources
      .map(vr => visitResources?.find(v => v.id === vr.id))
      .filter(filterOutUndefined),
    visitServices: visitTypeServices?.map(vts => ({
      id: Number(vts.service?.id) || 0,
      service: {
        id: vts.service?.id,
        name: vts.service?.name,
      }
    })) ?? []
  };

//

  return (
    <Card elevation={3} sx={{ width: 700 }}>
      <CardHeader
        sx={{
          borderBottom: 1,
          borderColor: "divider",
          flex: 1,
          flexDirection: "row",
        }}
        action={
          <IconButton aria-label="close" onClick={onClose}>
            <Close />
          </IconButton>
        }
        title={
          <Typography variant="h6">
            <Link to={`/patients/${visit.patient?.id}`}>{visit.patient?.firstName} {visit.patient?.lastName}</Link>
            {" - "}
            {visit.startTime && timeLabelWithZone(visit.startTime, true, visit.addressTimezone)}
          </Typography>
        }
      />
      <CardContent sx={{ pt: 0 }}>
        <Grid container direction="row" columnSpacing={1}>
          <Grid size={{ sm: 3 }}>
            <Tabs
              orientation="vertical"
              variant="standard"
              value={currentTab}
              onChange={(_, newValue) => setCurrentTab(newValue)}
            >
              <Tab value="details" label="Details" />
              <Tab value="patient" label="Patient Notes" />
            </Tabs>
          </Grid>
          <Grid size={{ sm: 9 }} sx={{ mt: 2 }}>
            <Box sx={{ overflow: "scroll", width: "100%", height: 400 }}>
              {currentTab === "details" && (
                <VisitDetailsTab visit={adaptedVisit} />
              )}
              <PatientTab visible={currentTab === "patient"} patient={visit.patient} />
            </Box>
          </Grid>
        </Grid>
      </CardContent>
      <CardActions
        sx={{ justifyContent: "end", borderTop: 1, borderColor: "divider" }}
      >
        <Button onClick={() => onRemoveVisit(visit)} color="error">
          Remove
        </Button>
      </CardActions>
    </Card>
  );
}

type PatientTabProps = {
  visible: boolean;
  patient: LocalVisit["patient"];
}

const PatientTab = ({ visible, patient }: PatientTabProps) => {
  return (
    <Box sx={{ display: visible ? "block" : "none", maxWidth: '320px', mt: 2 }}>
      <Typography variant="body1" gutterBottom sx={{ ml: 2 }}>
        {patient?.patientNotes || "No patient notes"}
      </Typography>
    </Box>
  )
}
